import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiClock, FiGlobe, FiUser, FiCheck, FiMinus, FiPlus } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { Card, Button, Badge, Spinner, EmptyState } from "../components/ui";
import { getCourse, enrollCourse, updateProgress, unenrollCourse } from "../api/courses";
import uploadUrl from "../utils/assetUrl";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { notify } = useToast();
  const confirm = useConfirm();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [newProgress, setNewProgress] = useState(0);

  const load = () => {
    setLoading(true);
    getCourse(id).then(res => {
      if (res.data?.success) {
        const c = res.data.course;
        setCourse(c);
        const myEnrollment = c.enrollments?.find(e => e.userId === user?.id);
        setEnrollment(myEnrollment || null);
        setNewProgress(myEnrollment?.progress || 0);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnroll = async () => {
    setActionBusy(true);
    try {
      const res = await enrollCourse(id);
      setEnrollment(res.data.enrollment);
      setNewProgress(0);
      notify("You're enrolled! Good luck.", "success");
      load();
    } catch { notify("Couldn't enroll. Please try again.", "error"); }
    finally { setActionBusy(false); }
  };

  const handleUnenroll = async () => {
    if (!(await confirm({ title: "Unenroll from this course?", description: "Your progress will be lost. This cannot be undone." }))) return;
    setActionBusy(true);
    try { await unenrollCourse(id); setEnrollment(null); notify("You've unenrolled.", "success"); load(); }
    catch { notify("Couldn't unenroll.", "error"); }
    finally { setActionBusy(false); }
  };

  const handleUpdateProgress = async () => {
    setActionBusy(true);
    try {
      const res = await updateProgress(id, newProgress);
      setEnrollment(res.data.enrollment);
      if (newProgress === 100) notify("🎉 Course completed! Well done.", "success");
      else notify("Progress updated.", "success");
      load();
    } catch { notify("Couldn't update progress.", "error"); }
    finally { setActionBusy(false); }
  };

  if (loading) return <AppShell title="Course"><div className="flex justify-center py-24"><Spinner className="h-8 w-8" /></div></AppShell>;
  if (!course) return <AppShell title="Course"><EmptyState title="Course not found" action={<Button onClick={() => navigate("/courses")}>Back to courses</Button>} /></AppShell>;

  return (
    <AppShell title={course.title} subtitle="Learning resource" wide>
      <button onClick={() => navigate("/courses")} className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <FiArrowLeft className="h-4 w-4" /> Back to courses
      </button>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card className="overflow-hidden">
            {course.image ? (
              <img src={uploadUrl(course.image)} alt={course.title} className="h-64 w-full object-cover" />
            ) : (
              <div className="flex h-48 items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white/60 text-6xl">📚</div>
            )}
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-3">
                {course.category && <Badge variant="primary">{course.category}</Badge>}
                {enrollment?.completed && <Badge variant="emerald">✓ Completed</Badge>}
              </div>
              <h2 className="mt-3 text-2xl font-bold text-slate-900">{course.title}</h2>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                {course.duration && <span className="flex items-center gap-1.5"><FiClock className="h-4 w-4" /> {course.duration}</span>}
                {course.tutor && <span className="flex items-center gap-1.5"><FiUser className="h-4 w-4" /> {course.tutor}</span>}
                <span className="flex items-center gap-1.5"><FiGlobe className="h-4 w-4" /> {course.languages || "English"}</span>
              </div>
              <p className="mt-4 leading-relaxed text-slate-600">{course.description}</p>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            {!enrollment ? (
              <>
                <p className="text-sm text-slate-600 mb-4">Join <strong>{course.enrollments?.length || 0}</strong> others learning this course.</p>
                <Button className="w-full" onClick={handleEnroll} loading={actionBusy}><FiPlus className="h-4 w-4" /> Enroll now</Button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-800">Your progress</p>
                  <span className="text-sm font-bold text-primary-600">{enrollment.progress}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${enrollment.progress}%` }} />
                </div>
                {enrollment.completed && <p className="mt-2 text-xs font-medium text-emerald-600">✓ Completed</p>}
                <div className="mt-4 space-y-3">
                  <label className="block text-xs font-medium text-slate-600">Update progress: {newProgress}%</label>
                  <input type="range" min={0} max={100} step={5} value={newProgress} onChange={e => setNewProgress(parseInt(e.target.value))} className="w-full accent-primary-600" />
                  <Button size="sm" className="w-full" onClick={handleUpdateProgress} loading={actionBusy}>
                    <FiCheck className="h-4 w-4" /> Save progress
                  </Button>
                </div>
                <Button size="sm" variant="outlineDanger" className="mt-3 w-full" onClick={handleUnenroll} loading={actionBusy}>
                  <FiMinus className="h-4 w-4" /> Unenroll
                </Button>
              </>
            )}
          </Card>

          <Card className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Learners ({course.enrollments?.length || 0})</p>
            {course.enrollments?.length === 0 ? (
              <p className="text-sm text-slate-400">Be the first to enroll.</p>
            ) : (
              <div className="space-y-2">
                {course.enrollments?.slice(0, 5).map(e => (
                  <div key={e.userId} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">{e.user?.name} {e.user?.lastName}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-primary-400" style={{ width: `${e.progress}%` }} /></div>
                      <span className="text-xs text-slate-400">{e.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
